import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Eye, 
  EyeOff, 
  User, 
  Lock, 
  Mail,
  Phone,
  FileText,
  Shield,
  Utensils,
  ArrowLeft,
  Sparkles,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import toast from 'react-hot-toast';

const ParentsRegisterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [formData, setFormData] = useState({
    parentName: '',
    parentEmail: '',
    parentPhone: '',
    parentCpf: '',
    studentName: '',
    studentEmail: '',
    registrationNumber: '',
    password: '',
    confirmPassword: '',
    acceptTerms: false
  });

  React.useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const validateForm = () => {
    if (!formData.parentName.trim()) {
      toast.error('Nome do responsável é obrigatório');
      return false;
    }

    if (!formData.parentEmail.trim()) {
      toast.error('Email do responsável é obrigatório');
      return false;
    }

    if (!formData.parentPhone.trim()) {
      toast.error('Telefone do responsável é obrigatório');
      return false;
    }

    if (!formData.studentName.trim()) {
      toast.error('Nome do estudante é obrigatório');
      return false;
    }

    if (!formData.registrationNumber.trim()) {
      toast.error('Número de matrícula é obrigatório');
      return false;
    }

    if (!formData.password || formData.password.length < 6) {
      toast.error('Senha deve ter pelo menos 6 caracteres');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Senhas não coincidem');
      return false;
    }

    if (!formData.acceptTerms) {
      toast.error('Você deve aceitar os termos de uso');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      // Aqui você implementaria a lógica de cadastro
      // Por enquanto, vamos simular um cadastro
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      toast.success('Cadastro realizado com sucesso! Aguarde aprovação da escola.');
      
      // Redirecionar para página de login após cadastro
      setTimeout(() => {
        navigate('/parents-portal/login');
      }, 2000);
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Erro ao realizar cadastro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white flex">
      {/* Cursor personalizado */}
      <div 
        className="fixed w-6 h-6 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full pointer-events-none z-50 mix-blend-difference transition-transform duration-100 ease-out"
        style={{
          left: mousePosition.x - 12,
          top: mousePosition.y - 12,
        }}
      />

      {/* Conteúdo principal */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 relative">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>

        <div className="relative w-full max-w-2xl space-y-8">
          {/* Header */}
          <div className="text-center">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl flex items-center justify-center">
                  <Utensils className="w-8 h-8 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
              </div>
            </div>

            {/* Títulos */}
            <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2">
              Cadastro de Responsável
            </h1>
            <p className="text-gray-400">
              Crie sua conta para acompanhar seu filho
            </p>

            {/* Badge de segurança */}
            <div className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-500/10 to-blue-500/10 border border-green-500/20 mt-4">
              <Shield className="w-4 h-4 mr-2 text-green-400" />
              <span className="text-xs text-green-300">🔒 Dados protegidos</span>
            </div>
          </div>

          {/* Formulário */}
          <div className="bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-700/50 p-8">
            <form className="space-y-6" onSubmit={handleSubmit}>
              {/* Dados do Responsável */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                  Dados do Responsável
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Nome do Responsável */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Nome Completo *
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type="text"
                        name="parentName"
                        placeholder="Seu nome completo"
                        value={formData.parentName}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* Email do Responsável */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Email *
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type="email"
                        name="parentEmail"
                        placeholder="seu@email.com"
                        value={formData.parentEmail}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* Telefone */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Telefone *
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Phone className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type="tel"
                        name="parentPhone"
                        placeholder="(11) 99999-9999"
                        value={formData.parentPhone}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* CPF */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      CPF
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FileText className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type="text"
                        name="parentCpf"
                        placeholder="000.000.000-00"
                        value={formData.parentCpf}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dados do Estudante */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                  Dados do Estudante
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Nome do Estudante */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Nome do Estudante *
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type="text"
                        name="studentName"
                        placeholder="Nome do seu filho(a)"
                        value={formData.studentName}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                        required
                      />
                    </div>
                  </div>

                  {/* Email do Estudante */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Email do Estudante
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type="email"
                        name="studentEmail"
                        placeholder="estudante@email.com"
                        value={formData.studentEmail}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                      />
                    </div>
                  </div>

                  {/* Número de Matrícula */}
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-medium text-gray-300">
                      Número de Matrícula *
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FileText className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type="text"
                        name="registrationNumber"
                        placeholder="Número de matrícula do estudante"
                        value={formData.registrationNumber}
                        onChange={handleChange}
                        className="w-full pl-10 pr-4 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Senha */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white border-b border-gray-700 pb-2">
                  Senha de Acesso
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Senha */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Senha *
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        placeholder="Mínimo 6 caracteres"
                        value={formData.password}
                        onChange={handleChange}
                        className="w-full pl-10 pr-12 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                        required
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Confirmar Senha */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-300">
                      Confirmar Senha *
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Lock className="w-5 h-5 text-gray-400 group-hover:text-blue-400 transition-colors" />
                      </div>
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        name="confirmPassword"
                        placeholder="Repita a senha"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        className="w-full pl-10 pr-12 py-3 bg-gray-800/50 border border-gray-600/50 rounded-xl text-white placeholder-gray-500 transition-all duration-300 focus:outline-none focus:ring-2 focus:border-blue-500/50 focus:ring-blue-500/20 hover:border-gray-500/50 backdrop-blur-sm"
                        required
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white transition-colors"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-5 h-5" />
                        ) : (
                          <Eye className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Termos e Condições */}
              <div className="flex items-start space-x-3">
                <input
                  id="acceptTerms"
                  name="acceptTerms"
                  type="checkbox"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-500 bg-gray-800 border-gray-600 rounded focus:ring-blue-500 focus:ring-2 mt-1"
                />
                <label htmlFor="acceptTerms" className="text-sm text-gray-300 leading-relaxed">
                  Aceito os{' '}
                  <Link to="/terms" className="text-blue-400 hover:text-blue-300 underline">
                    termos de uso
                  </Link>
                  {' '}e{' '}
                  <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline">
                    política de privacidade
                  </Link>
                  {' '}do Portal dos Pais
                </label>
              </div>

              {/* Alert Info */}
              <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-300">
                    <p className="font-medium mb-1">Importante:</p>
                    <p>Seu cadastro será analisado pela escola antes da aprovação. Você receberá um email quando sua conta estiver ativa.</p>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl hover:shadow-blue-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Cadastrando...
                  </div>
                ) : (
                  <div className="flex items-center justify-center">
                    <Sparkles className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                    Criar Conta
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl blur opacity-0 group-hover:opacity-50 transition-opacity duration-300 -z-10" />
              </button>
            </form>
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-gray-400 mb-4">Já possui uma conta?</p>
            <Link to="/parents-login">
              <button className="group w-full bg-gradient-to-br from-gray-800 to-gray-700 border border-gray-600 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 hover:border-blue-500/50 hover:shadow-lg hover:shadow-blue-500/10">
                <div className="flex items-center justify-center">
                  <ArrowLeft className="w-5 h-5 mr-2 group-hover:text-blue-400 transition-colors" />
                  Fazer Login
                </div>
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParentsRegisterPage;